import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { NumberInput } from "@/components/ui/number-input";
import { ClientPicker } from "@/components/ClientPicker";
import { toast } from "sonner";
import {
  Plus, Trash2, Save, ArrowLeft, Printer, Copy, Layers, Server, Boxes, FileText, Link2, X,
} from "lucide-react";
import logo from "@/assets/apex-av-new-logo-optimized.webp";

export const Route = createFileRoute("/tools/engineering")({
  head: () => ({
    meta: [
      { title: "Engineering Documents | Apex AV Tools" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EngineeringPage,
});

/* ---------------------------------- types --------------------------------- */

type Sheet = { id: string; title: string };
type Device = {
  id: string;
  sheet: string;
  manufacturer: string;
  model: string;
  label: string;
  zone: string;
  color: string;
  x: number;
  y: number;
  inputs: string[];
  outputs: string[];
};
type Conn = {
  id: string;
  from: string;
  fromPort: number;
  to: string;
  toPort: number;
  label: string;
  kind: keyof typeof KINDS;
  fromSide?: "in" | "out";
  toSide?: "in" | "out";
  color?: string;
};
type RackItem = { id: string; ru: number; size: number; label: string };
type Rack = { id: string; name: string; ru: number; items: RackItem[] };
type Doc = {
  id?: string;
  name: string;
  client_id: string | null;
  client_name: string | null;
  site_address: string | null;
  notes: string | null;
  revision: string | null;
  modified_on: string | null;
  sheets: Sheet[];
  devices: Device[];
  connections: Conn[];
  racks: Rack[];
};

const KINDS = {
  video: { label: "Video", color: "#1d4ed8" },
  audio: { label: "Audio", color: "#15803d" },
  control: { label: "Control", color: "#c026d3" },
  network: { label: "Network", color: "#ea580c" },
  power: { label: "Power", color: "#dc2626" },
} as const;

const COMPANY = {
  name: "Apex AV",
  address1: "2921 W 38th Ave STE 169",
  address2: "Denver, CO 80211",
  phone: "303.317.3233",
  web: "WWW.APEXAV.COM",
};

const DISCLAIMER =
  "These design documents are the exclusive property of Apex AV. They are prepared solely for use with this project and are not to be disseminated for use by any other individual or entity, or on any other project, without written consent of Apex AV.";

/* ------------------------------- geometry --------------------------------- */

const BLOCK_W = 300;
const HEAD_H = 20;
const ROW_H = 13;
const FOOT_H = 16;

const rowCount = (d: Device) => Math.max(d.inputs.length, d.outputs.length, 1);
const blockH = (d: Device) => HEAD_H + rowCount(d) * ROW_H + FOOT_H;
const inY = (d: Device, i: number) => d.y + HEAD_H + i * ROW_H + ROW_H / 2;
const outY = (d: Device, i: number) => d.y + HEAD_H + i * ROW_H + ROW_H / 2;

const uid = () => Math.random().toString(36).slice(2, 10);

/* ------------------------------ device presets ---------------------------- */

const PRESETS: { name: string; make: string; model: string; inputs: string[]; outputs: string[]; color: string }[] = [
  {
    name: "Control processor", make: "CONTROL4", model: "CORE 5", color: "#0f172a",
    inputs: ["POWER - 120VAC - PWR CORD", "LAN - NET - RJ45", "USB - DATA - USB"],
    outputs: ["IR 1 - CTL - 3.5MM", "IR 2 - CTL - 3.5MM", "RS-232 1 - CTL - RS232", "RELAY 1 - CTL - PHOENIX"],
  },
  {
    name: "Network switch", make: "ARAKNIS", model: "AN-310-SW-16-POE", color: "#0f172a",
    inputs: ["POWER - 120VAC - PWR CORD", "UPLINK - NET - RJ45"],
    outputs: Array.from({ length: 8 }, (_, i) => `PORT ${i + 1} - NET [PoE] - RJ45`),
  },
  {
    name: "AV receiver", make: "MARANTZ", model: "CINEMA 50", color: "#0f172a",
    inputs: ["POWER - 120VAC - PWR CORD", "HDMI 1 - HDMI - HDMI", "HDMI 2 - HDMI - HDMI", "LAN - NET - RJ45"],
    outputs: ["HDMI OUT 1 - HDMI - HDMI", "ZONE 2 - HDMI - HDMI", "SPKR - AUD - BPOST", "SUB 1 - AUD - RCA"],
  },
  {
    name: "Source / streamer", make: "APPLE", model: "APPLE TV 4K", color: "#0f172a",
    inputs: ["POWER - 120VAC - PWR CORD", "LAN - NET - RJ45", "IR IN - CTL - IR EYE"],
    outputs: ["HDMI - HDMI - HDMI"],
  },
  {
    name: "Display / TV", make: "SAMSUNG", model: "QN75LS03", color: "#b91c1c",
    inputs: ["POWER - 120VAC - PWR CORD", "HDMI 1 - HDMI - HDMI", "HDMI 2 [ARC] - HDMI - HDMI", "LAN - NET - RJ45"],
    outputs: ["OPTICAL - AUD - TOSLINK"],
  },
  {
    name: "NVR / cameras", make: "LUMA", model: "LUM-510-NVR-16CH", color: "#0f172a",
    inputs: ["POWER - 120VAC - PWR CORD", "LAN - NET - RJ45", ...Array.from({ length: 4 }, (_, i) => `CAMERA ${i + 1} - DATA [PoE] - RJ45`)],
    outputs: ["HDMI - VIDEO - HDMI"],
  },
  {
    name: "Amplifier", make: "EPISODE", model: "EA-AMP-8D-100", color: "#0f172a",
    inputs: ["POWER - 120VAC - PWR CORD", "LAN - NET - RJ45", "IN 1 - AUD - RCA", "IN 2 - AUD - RCA"],
    outputs: ["ZONE 1 - SPKR - BPOST", "ZONE 2 - SPKR - BPOST", "ZONE 3 - SPKR - BPOST", "ZONE 4 - SPKR - BPOST"],
  },
  {
    name: "Blank device", make: "MANUFACTURER", model: "MODEL", color: "#0f172a",
    inputs: ["POWER - 120VAC - PWR CORD"],
    outputs: ["OUT 1 - SIG - CONN"],
  },
];

/* ------------------------------- new doc ---------------------------------- */

const today = () => new Date().toISOString().slice(0, 10);

const newDoc = (): Doc => {
  const s1 = uid();
  const s2 = uid();
  return {
    name: "Untitled System Engineering",
    client_id: null,
    client_name: "",
    site_address: "",
    notes: "",
    revision: "A",
    modified_on: today(),
    sheets: [
      { id: s1, title: "Video Schematic" },
      { id: s2, title: "Control Schematic" },
    ],
    devices: [],
    connections: [],
    racks: [{ id: uid(), name: "Main Rack", ru: 24, items: [] }],
  };
};

/* --------------------------------- page ----------------------------------- */

function EngineeringPage() {
  const [list, setList] = useState<Doc[]>([]);
  const [doc, setDoc] = useState<Doc | null>(null);
  const [tab, setTab] = useState<"info" | "schematic" | "racks">("schematic");
  const [activeSheet, setActiveSheet] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from("engineering_docs")
      .select("*")
      .order("updated_at", { ascending: false });
    if (error) return;
    setList((data as unknown as Doc[]) || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = (d: Doc) => {
    setDoc(JSON.parse(JSON.stringify(d)));
    setActiveSheet(d.sheets[0]?.id || "");
    setTab("schematic");
  };

  const create = () => {
    const d = newDoc();
    setDoc(d);
    setActiveSheet(d.sheets[0].id);
    setTab("info");
  };

  const save = async (asNew = false) => {
    if (!doc) return;
    setSaving(true);
    const payload = {
      name: doc.name,
      client_id: doc.client_id,
      client_name: doc.client_name,
      site_address: doc.site_address,
      notes: doc.notes,
      revision: doc.revision,
      modified_on: doc.modified_on || today(),
      sheets: doc.sheets as unknown as never,
      devices: doc.devices as unknown as never,
      connections: doc.connections as unknown as never,
      racks: doc.racks as unknown as never,
    };
    if (doc.id && !asNew) {
      const { error } = await supabase.from("engineering_docs").update(payload).eq("id", doc.id);
      if (error) { toast.error(error.message); setSaving(false); return; }
      toast.success("Document saved");
    } else {
      const { data, error } = await supabase
        .from("engineering_docs")
        .insert({ ...payload, name: asNew ? `${doc.name} (copy)` : doc.name })
        .select("id,name")
        .single();
      if (error) { toast.error(error.message); setSaving(false); return; }
      setDoc({ ...doc, id: data!.id as string, name: data!.name as string });
      toast.success("Document created");
    }
    setSaving(false);
    load();
  };

  const remove = async (id?: string) => {
    if (!id) return;
    if (!confirm("Delete this engineering document?")) return;
    await supabase.from("engineering_docs").delete().eq("id", id);
    setDoc(null);
    load();
  };

  /* ----------------------------- list view ------------------------------- */
  if (!doc) {
    return (
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground">Engineering Documents</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Build system engineering packages — cover sheet, rack elevations, and wired schematics with a printable title block.
            </p>
          </div>
          <Button onClick={create}><Plus className="mr-2 h-4 w-4" />New document</Button>
        </div>

        <div className="mt-8 grid gap-3">
          {list.length === 0 && (
            <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              No documents yet. Create your first system engineering package.
            </p>
          )}
          {list.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-4">
              <div>
                <div className="font-medium text-foreground">{d.name}</div>
                <div className="text-xs text-muted-foreground">
                  {d.client_name || "No customer"} · {d.sheets?.length || 0} sheets · {d.devices?.length || 0} devices
                  {d.modified_on ? ` · modified ${d.modified_on}` : ""}
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => open(d)}>Open</Button>
                <Button variant="ghost" size="sm" onClick={() => remove(d.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ----------------------------- editor view ----------------------------- */
  return (
    <div>
      <div className="no-print">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setDoc(null)}>
              <ArrowLeft className="mr-2 h-4 w-4" />All documents
            </Button>
            <Input
              value={doc.name}
              onChange={(e) => setDoc({ ...doc, name: e.target.value })}
              className="w-72 font-medium"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => window.print()}>
              <Printer className="mr-2 h-4 w-4" />Print / PDF
            </Button>
            <Button variant="outline" size="sm" disabled={saving} onClick={() => save(true)}>
              <Copy className="mr-2 h-4 w-4" />Save as…
            </Button>
            <Button size="sm" disabled={saving} onClick={() => save(false)}>
              <Save className="mr-2 h-4 w-4" />Save
            </Button>
          </div>
        </div>

        <div className="mt-4 flex gap-2 border-b border-border">
          {([
            ["info", "Project info", FileText],
            ["schematic", "Schematics", Layers],
            ["racks", "Rack elevations", Server],
          ] as const).map(([key, label, Icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`-mb-px flex items-center gap-2 border-b-2 px-3 py-2 text-sm ${
                tab === key ? "border-foreground font-medium text-foreground" : "border-transparent text-muted-foreground"
              }`}
            >
              <Icon className="h-4 w-4" />{label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "info" && <InfoTab doc={doc} setDoc={setDoc} />}
          {tab === "schematic" && (
            <SchematicTab doc={doc} setDoc={setDoc} activeSheet={activeSheet} setActiveSheet={setActiveSheet} />
          )}
          {tab === "racks" && <RackTab doc={doc} setDoc={setDoc} />}
        </div>
      </div>

      <PrintPackage doc={doc} />
      <PrintStyles />
    </div>
  );
}

/* -------------------------------- info tab -------------------------------- */

function InfoTab({ doc, setDoc }: { doc: Doc; setDoc: (d: Doc) => void }) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-4 rounded-lg border border-border bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Project</h2>
        <ClientPicker
          clientId={doc.client_id}
          clientName={doc.client_name}
          onPick={(f) =>
            setDoc({
              ...doc,
              client_id: f.client_id,
              client_name: f.client_name,
              site_address: doc.site_address || f.client_address,
            })
          }
        />
        <div>
          <label className="text-xs font-medium text-muted-foreground">Site address</label>
          <Input value={doc.site_address || ""} onChange={(e) => setDoc({ ...doc, site_address: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Revision</label>
            <Input value={doc.revision || ""} onChange={(e) => setDoc({ ...doc, revision: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Modified</label>
            <Input type="date" value={doc.modified_on || ""} onChange={(e) => setDoc({ ...doc, modified_on: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground">Notes (shown in title block)</label>
          <Textarea rows={4} value={doc.notes || ""} onChange={(e) => setDoc({ ...doc, notes: e.target.value })} />
        </div>
      </div>

      <div className="space-y-3 rounded-lg border border-border bg-card p-5">
        <h2 className="font-display text-lg font-semibold">Sheets</h2>
        <p className="text-xs text-muted-foreground">
          A cover sheet and rack elevation sheet are generated automatically. Add schematic sheets below.
        </p>
        {doc.sheets.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2">
            <span className="w-6 text-xs text-muted-foreground">{i + 3}</span>
            <Input
              value={s.title}
              onChange={(e) =>
                setDoc({ ...doc, sheets: doc.sheets.map((x) => (x.id === s.id ? { ...x, title: e.target.value } : x)) })
              }
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setDoc({
                  ...doc,
                  sheets: doc.sheets.filter((x) => x.id !== s.id),
                  devices: doc.devices.filter((d) => d.sheet !== s.id),
                })
              }
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDoc({ ...doc, sheets: [...doc.sheets, { id: uid(), title: "New Schematic" }] })}
        >
          <Plus className="mr-2 h-4 w-4" />Add sheet
        </Button>
      </div>
    </div>
  );
}

/* ----------------------------- schematic tab ------------------------------ */

function SchematicTab({
  doc, setDoc, activeSheet, setActiveSheet,
}: {
  doc: Doc; setDoc: (d: Doc) => void; activeSheet: string; setActiveSheet: (s: string) => void;
}) {
  const sheetId = doc.sheets.some((s) => s.id === activeSheet) ? activeSheet : doc.sheets[0]?.id || "";
  const [zoom, setZoom] = useState(0.85);
  const [pending, setPending] = useState<{ id: string; port: number; side: "in" | "out" } | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [selConn, setSelConn] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; kind: "device" | "conn"; id: string } | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ id: string; dx: number; dy: number } | null>(null);


  const devices = doc.devices.filter((d) => d.sheet === sheetId);
  const conns = doc.connections.filter((c) => {
    const a = doc.devices.find((d) => d.id === c.from);
    const b = doc.devices.find((d) => d.id === c.to);
    return a && b && a.sheet === sheetId && b.sheet === sheetId;
  });
  const sel = doc.devices.find((d) => d.id === selected) || null;

  const addPreset = (p: (typeof PRESETS)[number]) => {
    const d: Device = {
      id: uid(), sheet: sheetId, manufacturer: p.make, model: p.model, label: "",
      zone: "", color: p.color, x: 60 + (devices.length % 4) * 340, y: 60 + Math.floor(devices.length / 4) * 220,
      inputs: [...p.inputs], outputs: [...p.outputs],
    };
    setDoc({ ...doc, devices: [...doc.devices, d] });
    setSelected(d.id);
  };

  const patch = (id: string, p: Partial<Device>) =>
    setDoc({ ...doc, devices: doc.devices.map((d) => (d.id === id ? { ...d, ...p } : d)) });

  const removeDevice = (id: string) =>
    setDoc({
      ...doc,
      devices: doc.devices.filter((d) => d.id !== id),
      connections: doc.connections.filter((c) => c.from !== id && c.to !== id),
    });

  const onPointerDown = (e: React.PointerEvent, d: Device) => {
    const rect = wrapRef.current!.getBoundingClientRect();
    dragRef.current = {
      id: d.id,
      dx: (e.clientX - rect.left) / zoom + wrapRef.current!.scrollLeft / zoom - d.x,
      dy: (e.clientY - rect.top) / zoom + wrapRef.current!.scrollTop / zoom - d.y,
    };
    setSelected(d.id);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const x = Math.max(0, (e.clientX - rect.left) / zoom + wrapRef.current.scrollLeft / zoom - drag.dx);
    const y = Math.max(0, (e.clientY - rect.top) / zoom + wrapRef.current.scrollTop / zoom - drag.dy);
    patch(drag.id, { x: Math.round(x), y: Math.round(y) });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const clickPort = (id: string, port: number, side: "in" | "out") => {
    if (!pending) { setPending({ id, port, side }); return; }
    if (pending.id === id && pending.port === port && pending.side === side) { setPending(null); return; }
    // Any port can connect to any port — an output start keeps its direction,
    // an input start becomes the destination.
    const src = pending.side === "out" ? pending : { id, port, side };
    const dst = pending.side === "out" ? { id, port, side } : pending;
    const c: Conn = {
      id: uid(),
      from: src.id, fromPort: src.port, fromSide: src.side,
      to: dst.id, toPort: dst.port, toSide: dst.side,
      label: `${doc.devices.find((d) => d.id === src.id)?.label || "SIG"}-${doc.connections.length + 1}`,
      kind: "video",
    };
    setDoc({ ...doc, connections: [...doc.connections, c] });
    setPending(null);
  };

  const removeConn = (id: string) => {
    setDoc({ ...doc, connections: doc.connections.filter((c) => c.id !== id) });
    setSelConn((s) => (s === id ? null : s));
  };

  const patchConn = (id: string, p: Partial<Conn>) =>
    setDoc({ ...doc, connections: doc.connections.map((c) => (c.id === id ? { ...c, ...p } : c)) });



  const deleteDevice = (id: string) => {
    removeDevice(id);
    setSelected((s) => (s === id ? null : s));
  };

  // Delete / Backspace removes the selected block or wire
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (selConn) { e.preventDefault(); removeConn(selConn); return; }
      if (selected) { e.preventDefault(); deleteDevice(selected); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [menu]);


  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
      <div className="order-2 lg:order-1">

        <div className="mb-3 flex flex-wrap items-center gap-2">
          {doc.sheets.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSheet(s.id)}
              className={`rounded-md border px-3 py-1.5 text-sm ${
                s.id === sheetId ? "border-foreground bg-accent font-medium" : "border-border text-muted-foreground"
              }`}
            >
              {s.title}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
            <span>Zoom</span>
            <input type="range" min={0.4} max={1.4} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
            <span>{Math.round(zoom * 100)}%</span>
          </div>
        </div>

        {pending && (
          <div className="mb-2 flex items-center gap-2 rounded-md border border-brand/40 bg-accent px-3 py-2 text-xs">
            <Link2 className="h-3.5 w-3.5" />
            Wiring from {pending.side === "out" ? "an output" : "an input"} — click any other port to connect.
            <Button variant="ghost" size="sm" className="ml-auto h-6" onClick={() => setPending(null)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        <p className="mb-2 text-[11px] text-muted-foreground">
          Click any port then any other port to wire them. Right-click a device to delete/duplicate, or a wire to rename, recolor, or delete it.
        </p>

        <div
          ref={wrapRef}
          className="relative h-[60vh] overflow-auto rounded-lg border border-border bg-white lg:h-[70vh]"
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onContextMenu={(e) => { e.preventDefault(); setMenu(null); }}
        >
          <div style={{ width: 2600 * zoom, height: 1800 * zoom }}>
            <div style={{ width: 2600, height: 1800, transform: `scale(${zoom})`, transformOrigin: "top left" }} className="relative">
              <Wires
                devices={doc.devices}
                conns={conns}
                selectedId={selConn}
                onSelect={(id) => { setSelConn(id); setSelected(null); }}
                onContext={(id, e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelConn(id);
                  setMenu({ x: e.clientX, y: e.clientY, kind: "conn", id });
                }}
              />
              {devices.map((d) => (
                <BlockView
                  key={d.id}
                  d={d}
                  selected={selected === d.id}
                  onHeaderDown={(e) => onPointerDown(e, d)}
                  onPort={(port, side) => clickPort(d.id, port, side)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelected(d.id);
                    setSelConn(null);
                    setMenu({ x: e.clientX, y: e.clientY, kind: "device", id: d.id });
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {menu && (
          <div
            className="fixed z-50 w-52 overflow-hidden rounded-md border border-border bg-popover py-1 shadow-lg"
            style={{ left: menu.x, top: menu.y }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-3 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
              {menu.kind === "device"
                ? doc.devices.find((d) => d.id === menu.id)?.model || "Device"
                : doc.connections.find((c) => c.id === menu.id)?.label || "Connection"}
            </div>
            {menu.kind === "conn" && (() => {
              const c = doc.connections.find((x) => x.id === menu.id);
              if (!c) return null;
              const current = c.color || KINDS[c.kind]?.color || "#1d4ed8";
              return (
                <div className="space-y-2 border-b border-border px-3 pb-3 pt-1">
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Label</label>
                    <Input
                      autoFocus
                      value={c.label}
                      className="h-7 text-xs"
                      onChange={(e) => patchConn(c.id, { label: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Signal type</label>
                    <select
                      value={c.kind}
                      className="h-7 w-full rounded-md border border-border bg-background px-1 text-xs"
                      onChange={(e) =>
                        patchConn(c.id, { kind: e.target.value as Conn["kind"], color: undefined })
                      }
                    >
                      {Object.entries(KINDS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wide text-muted-foreground">Color</label>
                    <div className="flex items-center gap-1.5">
                      {Object.values(KINDS).map((v) => (
                        <button
                          key={v.color}
                          title={v.label}
                          onClick={() => patchConn(c.id, { color: v.color })}
                          className="h-5 w-5 rounded-full border border-border"
                          style={{ background: v.color, outline: current === v.color ? "2px solid hsl(var(--ring))" : undefined }}
                        />
                      ))}
                      <input
                        type="color"
                        value={current}
                        onChange={(e) => patchConn(c.id, { color: e.target.value })}
                        className="h-6 w-8 cursor-pointer rounded border border-border bg-transparent p-0"
                      />
                    </div>
                  </div>
                </div>
              );
            })()}
            {menu.kind === "device" && (
              <button
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                onClick={() => {
                  const d = doc.devices.find((x) => x.id === menu.id);
                  if (d) {
                    const copy: Device = { ...d, id: uid(), x: d.x + 30, y: d.y + 30 };
                    setDoc({ ...doc, devices: [...doc.devices, copy] });
                    setSelected(copy.id);
                  }
                  setMenu(null);
                }}
              >
                <Copy className="h-3.5 w-3.5" />Duplicate device
              </button>
            )}
            <button
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-destructive hover:bg-accent"
              onClick={() => {
                if (menu.kind === "device") deleteDevice(menu.id);
                else removeConn(menu.id);
                setMenu(null);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {menu.kind === "device" ? "Delete device + its wires" : "Delete connection"}
            </button>
          </div>
        )}

      </div>

      <div className="order-1 space-y-4 lg:order-2 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-auto">
        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-2 flex items-center gap-2 font-display text-sm font-semibold">
            <Boxes className="h-4 w-4" />Add device
          </h3>
          <div className="grid grid-cols-2 gap-1.5 lg:grid-cols-1">
            {PRESETS.map((p) => (
              <button
                key={p.name}
                onClick={() => addPreset(p)}
                className="rounded-md border border-border px-2 py-1.5 text-left text-xs hover:bg-accent"
              >
                <span className="font-medium">{p.name}</span>
                <span className="block text-[11px] text-muted-foreground">{p.make} {p.model}</span>
              </button>
            ))}
          </div>
        </div>

        {sel ? (
          <DeviceEditor
            d={sel}
            onChange={(p) => patch(sel.id, p)}
            onDelete={() => { removeDevice(sel.id); setSelected(null); }}
          />
        ) : (
          <p className="rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground">
            Select a block to edit its ports, label, and colors. Drag a block by its header to move it.
          </p>
        )}

        <div className="rounded-lg border border-border bg-card p-4">
          <h3 className="mb-2 font-display text-sm font-semibold">Connections ({conns.length})</h3>
          <div className="max-h-64 space-y-1.5 overflow-auto">
            {conns.map((c) => {
              const a = doc.devices.find((d) => d.id === c.from)!;
              const b = doc.devices.find((d) => d.id === c.to)!;
              return (
                <div key={c.id} className="rounded-md border border-border p-2 text-[11px]">
                  <div className="flex items-center gap-1">
                    <Input
                      value={c.label}
                      onChange={(e) =>
                        setDoc({ ...doc, connections: doc.connections.map((x) => (x.id === c.id ? { ...x, label: e.target.value } : x)) })
                      }
                      className="h-7 text-[11px]"
                    />
                    <select
                      value={c.kind}
                      className="h-7 rounded-md border border-border bg-background px-1 text-[11px]"
                      onChange={(e) => patchConn(c.id, { kind: e.target.value as Conn["kind"], color: undefined })}
                    >
                      {Object.entries(KINDS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                    <input
                      type="color"
                      value={c.color || KINDS[c.kind]?.color || "#1d4ed8"}
                      onChange={(e) => patchConn(c.id, { color: e.target.value })}
                      className="h-7 w-8 cursor-pointer rounded border border-border bg-transparent p-0"
                    />
                    <Button
                      variant="ghost" size="sm" className="h-7 px-1"
                      onClick={() => setDoc({ ...doc, connections: doc.connections.filter((x) => x.id !== c.id) })}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="mt-1 text-muted-foreground">
                    {a.model} · {(c.fromSide === "in" ? a.inputs : a.outputs)[c.fromPort]} → {b.model} ·{" "}
                    {(c.toSide === "out" ? b.outputs : b.inputs)[c.toPort]}
                  </div>
                </div>
              );
            })}
            {conns.length === 0 && <p className="text-[11px] text-muted-foreground">Click any port, then any other port to wire devices.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function DeviceEditor({ d, onChange, onDelete }: { d: Device; onChange: (p: Partial<Device>) => void; onDelete: () => void }) {
  const editList = (key: "inputs" | "outputs") => (
    <div className="space-y-1">
      {d[key].map((p, i) => (
        <div key={i} className="flex gap-1">
          <Input
            value={p}
            className="h-7 text-[11px]"
            onChange={(e) => onChange({ [key]: d[key].map((x, idx) => (idx === i ? e.target.value : x)) } as Partial<Device>)}
          />
          <Button
            variant="ghost" size="sm" className="h-7 px-1"
            onClick={() => onChange({ [key]: d[key].filter((_, idx) => idx !== i) } as Partial<Device>)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ))}
      <Button
        variant="outline" size="sm" className="h-7 text-[11px]"
        onClick={() => onChange({ [key]: [...d[key], key === "inputs" ? "IN - SIG - CONN" : "OUT - SIG - CONN"] } as Partial<Device>)}
      >
        <Plus className="mr-1 h-3 w-3" />Add {key === "inputs" ? "input" : "output"}
      </Button>
    </div>
  );

  return (
    <div className="space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold">Block</h3>
        <Button variant="ghost" size="sm" onClick={onDelete}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input className="h-8 text-xs" value={d.manufacturer} onChange={(e) => onChange({ manufacturer: e.target.value })} placeholder="Manufacturer" />
        <Input className="h-8 text-xs" value={d.model} onChange={(e) => onChange({ model: e.target.value })} placeholder="Model" />
        <Input className="h-8 text-xs" value={d.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Tag (VID-1)" />
        <Input className="h-8 text-xs" value={d.zone} onChange={(e) => onChange({ zone: e.target.value })} placeholder="Room / zone" />
      </div>
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Header color</span>
        <input type="color" value={d.color} onChange={(e) => onChange({ color: e.target.value })} className="h-7 w-10 rounded border border-border" />
      </div>
      <div>
        <div className="mb-1 text-[11px] font-medium text-muted-foreground">Inputs</div>
        {editList("inputs")}
      </div>
      <div>
        <div className="mb-1 text-[11px] font-medium text-muted-foreground">Outputs</div>
        {editList("outputs")}
      </div>
    </div>
  );
}

function BlockView({
  d, selected, onHeaderDown, onPort, print, onContextMenu,
}: {
  d: Device; selected?: boolean; onHeaderDown?: (e: React.PointerEvent) => void;
  onPort?: (port: number, side: "in" | "out") => void; print?: boolean;
  onContextMenu?: (e: React.MouseEvent) => void;
}) {
  const rows = rowCount(d);
  return (
    <div
      className="absolute border border-black bg-white"
      onContextMenu={onContextMenu}
      style={{ left: d.x, top: d.y, width: BLOCK_W, outline: selected ? "2px solid #6366f1" : undefined }}
    >

      <div
        onPointerDown={onHeaderDown}
        className={print ? "" : "cursor-move"}
        style={{ background: d.color, color: "#fff", height: HEAD_H, display: "flex", fontSize: 9, fontWeight: 700, letterSpacing: 0.3 }}
      >
        <div style={{ flex: 1, borderRight: "1px solid rgba(255,255,255,.4)", padding: "4px 5px" }}>{d.manufacturer}</div>
        <div style={{ flex: 1, padding: "4px 5px", textAlign: "right" }}>{d.model}</div>
      </div>
      <div style={{ position: "relative" }}>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} style={{ display: "flex", height: ROW_H, fontSize: 6.5, lineHeight: `${ROW_H}px`, borderTop: i ? "1px solid #eee" : undefined }}>
            <div
              onClick={() => d.inputs[i] !== undefined && onPort?.(i, "in")}
              style={{ flex: 1, padding: "0 4px", cursor: d.inputs[i] !== undefined && !print ? "pointer" : "default", color: "#111" }}
            >
              {d.inputs[i] || ""}
            </div>
            <div
              onClick={() => d.outputs[i] !== undefined && onPort?.(i, "out")}
              style={{ flex: 1, padding: "0 4px", textAlign: "right", cursor: d.outputs[i] !== undefined && !print ? "pointer" : "default", color: "#111" }}
            >
              {d.outputs[i] || ""}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", height: FOOT_H, borderTop: "1px solid #000", fontSize: 7, fontWeight: 700, lineHeight: `${FOOT_H}px` }}>
        <div style={{ flex: 1, padding: "0 4px" }}>IN</div>
        <div style={{ flex: 1, textAlign: "center" }}>{d.label}</div>
        <div style={{ flex: 1, padding: "0 4px", textAlign: "right" }}>OUT</div>
      </div>
      {d.zone && (
        <div style={{ position: "absolute", top: "100%", left: 0, width: BLOCK_W, textAlign: "center", fontSize: 7, color: "#444" }}>
          {d.zone}
        </div>
      )}
    </div>
  );
}

function Wires({
  devices, conns, selectedId, onSelect, onContext,
}: {
  devices: Device[]; conns: Conn[]; selectedId?: string | null;
  onSelect?: (id: string) => void;
  onContext?: (id: string, e: React.MouseEvent) => void;
}) {
  const interactive = Boolean(onSelect || onContext);
  return (
    <svg
      className={`absolute inset-0 h-full w-full ${interactive ? "" : "pointer-events-none"}`}
      style={{ overflow: "visible", pointerEvents: interactive ? "none" : undefined }}
    >
      {conns.map((c) => {
        const a = devices.find((d) => d.id === c.from);
        const b = devices.find((d) => d.id === c.to);
        if (!a || !b) return null;
        const aOut = c.fromSide !== "in";
        const bIn = c.toSide !== "out";
        const x1 = aOut ? a.x + BLOCK_W : a.x;
        const y1 = aOut ? outY(a, c.fromPort) : inY(a, c.fromPort);
        const x2 = bIn ? b.x : b.x + BLOCK_W;
        const y2 = bIn ? inY(b, c.toPort) : outY(b, c.toPort);
        const s1 = x1 + (aOut ? 24 : -24);
        const s2 = x2 + (bIn ? -24 : 24);
        const mid = (s1 + s2) / 2;
        const path = `M ${x1} ${y1} H ${s1} H ${mid} V ${y2} H ${s2} H ${x2}`;
        const color = c.color || KINDS[c.kind]?.color || "#1d4ed8";
        const on = selectedId === c.id;
        return (
          <g key={c.id}>
            {interactive && (
              <path
                d={path}
                fill="none"
                stroke="transparent"
                strokeWidth={10}
                style={{ pointerEvents: "stroke", cursor: "pointer" }}
                onClick={(e) => { e.stopPropagation(); onSelect?.(c.id); }}
                onContextMenu={(e) => onContext?.(c.id, e)}
              />
            )}
            <path d={path} fill="none" stroke={color} strokeWidth={on ? 2.5 : 1} strokeDasharray="4 3" />
            <circle cx={x2} cy={y2} r={2} fill={color} />
            {c.label && (
              <text x={mid + 3} y={(y1 + y2) / 2 - 2} fontSize={7} fill={color} fontWeight={on ? 700 : 400}>{c.label}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}


/* -------------------------------- rack tab -------------------------------- */

function RackTab({ doc, setDoc }: { doc: Doc; setDoc: (d: Doc) => void }) {
  const setRacks = (racks: Rack[]) => setDoc({ ...doc, racks });
  return (
    <div className="space-y-6">
      <Button
        variant="outline" size="sm"
        onClick={() => setRacks([...doc.racks, { id: uid(), name: `Rack ${doc.racks.length + 1}`, ru: 24, items: [] }])}
      >
        <Plus className="mr-2 h-4 w-4" />Add rack
      </Button>
      <div className="grid gap-6 lg:grid-cols-2">
        {doc.racks.map((r) => (
          <div key={r.id} className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center gap-2">
              <Input value={r.name} className="h-8" onChange={(e) => setRacks(doc.racks.map((x) => (x.id === r.id ? { ...x, name: e.target.value } : x)))} />
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                RU
                <NumberInput
                  value={r.ru}
                  min={1}
                  className="h-8 w-20"
                  onValueChange={(v: number) => setRacks(doc.racks.map((x) => (x.id === r.id ? { ...x, ru: Number(v) || 1 } : x)))}
                />
              </div>
              <Button variant="ghost" size="sm" onClick={() => setRacks(doc.racks.filter((x) => x.id !== r.id))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            <div className="mt-3 space-y-1.5">
              {r.items
                .slice()
                .sort((a, b) => b.ru - a.ru)
                .map((it) => (
                  <div key={it.id} className="flex items-center gap-1.5">
                    <NumberInput
                      value={it.ru} min={1} max={r.ru} className="h-8 w-16"
                      onValueChange={(v: number) => setRacks(doc.racks.map((x) => (x.id === r.id ? { ...x, items: x.items.map((y) => (y.id === it.id ? { ...y, ru: Number(v) || 1 } : y)) } : x)))}
                    />
                    <NumberInput
                      value={it.size} min={1} max={r.ru} className="h-8 w-16"
                      onValueChange={(v: number) => setRacks(doc.racks.map((x) => (x.id === r.id ? { ...x, items: x.items.map((y) => (y.id === it.id ? { ...y, size: Number(v) || 1 } : y)) } : x)))}
                    />
                    <Input
                      value={it.label} className="h-8 text-xs"
                      onChange={(e) => setRacks(doc.racks.map((x) => (x.id === r.id ? { ...x, items: x.items.map((y) => (y.id === it.id ? { ...y, label: e.target.value } : y)) } : x)))}
                    />
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setRacks(doc.racks.map((x) => (x.id === r.id ? { ...x, items: x.items.filter((y) => y.id !== it.id) } : x)))}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              <Button
                variant="outline" size="sm"
                onClick={() =>
                  setRacks(doc.racks.map((x) => (x.id === r.id ? { ...x, items: [...x.items, { id: uid(), ru: 1, size: 1, label: "Equipment" }] } : x)))
                }
              >
                <Plus className="mr-1 h-3 w-3" />Add equipment
              </Button>
              <p className="text-[11px] text-muted-foreground">Columns: starting RU · size (RU) · label</p>
            </div>

            <div className="mt-4">
              <RackElevation rack={r} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RackElevation({ rack }: { rack: Rack }) {
  const rows = Array.from({ length: rack.ru }, (_, i) => rack.ru - i);
  const occupied = new Map<number, RackItem>();
  const spanned = new Set<number>();
  for (const it of rack.items) {
    occupied.set(it.ru, it);
    for (let k = 1; k < it.size; k++) spanned.add(it.ru + k);
  }
  return (
    <div className="border border-black bg-white text-black">
      <div className="border-b border-black bg-neutral-100 px-2 py-1 text-center text-[10px] font-bold">{rack.name}</div>
      {rows.map((ru) => {
        const it = occupied.get(ru);
        if (spanned.has(ru)) return null;
        const h = it ? it.size * 16 : 16;
        return (
          <div key={ru} className="flex border-b border-neutral-300" style={{ height: h }}>
            <div className="w-8 border-r border-neutral-300 bg-neutral-50 text-center text-[9px] leading-4">
              {String(ru).padStart(2, "0")}
            </div>
            <div className="flex flex-1 items-center justify-center px-1 text-[9px]" style={{ background: it ? "#eef2ff" : undefined }}>
              {it?.label || ""}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------ print package ----------------------------- */

const SHEET_W = 1584; // 16.5in @ 96dpi
const SHEET_H = 1044; // 10.875in
const BLOCK_PANEL = 300;

function TitleBlock({ doc, sheetTitle, sheetNo }: { doc: Doc; sheetTitle: string; sheetNo: number }) {
  return (
    <div className="flex h-full flex-col justify-between border-l border-black px-4 py-4 text-black" style={{ width: BLOCK_PANEL }}>
      <div>
        <img src={logo} alt="Apex AV" style={{ height: 54, objectFit: "contain" }} />
        <div className="mt-4 text-[11px] leading-5">
          <div>{COMPANY.address1}</div>
          <div>{COMPANY.address2}</div>
          <div>{COMPANY.phone}</div>
          <div>{COMPANY.web}</div>
        </div>
      </div>

      <div className="border-t border-black pt-3 text-[12px]">
        <div className="text-[20px] font-bold leading-tight">{doc.client_name || COMPANY.name}</div>
        <div className="mt-1 whitespace-pre-line text-[11px] leading-4">{doc.site_address}</div>
      </div>

      <div className="border-t border-black pt-3 text-[11px]">
        <div className="font-bold">Notes:</div>
        <div className="whitespace-pre-line leading-4">{doc.notes}</div>
        <div className="mt-3"><span className="font-bold">Modified </span>{doc.modified_on}</div>
        <div><span className="font-bold">Revision </span>{doc.revision}</div>
      </div>

      <div className="border-t border-black pt-3 text-[11px]">
        <div className="font-bold">Project:</div>
        <div className="leading-4">{doc.name}</div>
      </div>

      <div className="border-t border-black pt-3">
        <div className="text-[15px] font-bold">{sheetTitle}</div>
        <div className="mt-2 text-[26px] font-bold">{sheetNo}</div>
      </div>

      <div className="border-t border-black pt-2 text-[6.5px] leading-[9px]">{DISCLAIMER}</div>
    </div>
  );
}

function PrintSheet({ doc, title, no, children }: { doc: Doc; title: string; no: number; children: React.ReactNode }) {
  return (
    <div className="print-sheet" style={{ width: SHEET_W, height: SHEET_H }}>
      <div className="flex h-full w-full border border-black bg-white">
        <div className="relative flex-1 overflow-hidden">{children}</div>
        <TitleBlock doc={doc} sheetTitle={title} sheetNo={no} />
      </div>
    </div>
  );
}

function PrintPackage({ doc }: { doc: Doc }) {
  const contentW = SHEET_W - BLOCK_PANEL - 4;
  const contentH = SHEET_H - 4;

  const sheetScale = (devices: Device[]) => {
    if (devices.length === 0) return { k: 1, ox: 0, oy: 0 };
    const maxX = Math.max(...devices.map((d) => d.x + BLOCK_W));
    const maxY = Math.max(...devices.map((d) => d.y + blockH(d) + 14));
    const minX = Math.min(...devices.map((d) => d.x));
    const minY = Math.min(...devices.map((d) => d.y));
    const k = Math.min((contentW - 40) / (maxX - minX), (contentH - 40) / (maxY - minY), 1);
    return { k, ox: minX, oy: minY };
  };

  return (
    <div className="print-only">
      {/* Cover sheet */}
      <PrintSheet doc={doc} title="Cover Sheet" no={1}>
        <div className="flex h-full flex-col items-center justify-center gap-6 text-black">
          <img src={logo} alt="Apex AV" style={{ height: 120, objectFit: "contain" }} />
          <div className="text-center">
            <div className="text-[34px] font-bold">System Engineering</div>
            <div className="mt-2 text-[20px]">{doc.client_name}</div>
            <div className="whitespace-pre-line text-[15px]">{doc.site_address}</div>
          </div>
          <div className="mt-6 w-2/3 border-t border-black pt-4 text-center text-[13px]">
            <div className="font-bold">Sheet index</div>
            <div className="mt-2 grid grid-cols-2 gap-x-10 gap-y-1 text-left">
              <div>1 — Cover Sheet</div>
              <div>2 — Rack Elevations</div>
              {doc.sheets.map((s, i) => (
                <div key={s.id}>{i + 3} — {s.title}</div>
              ))}
            </div>
          </div>
        </div>
      </PrintSheet>

      {/* Rack elevations */}
      <PrintSheet doc={doc} title="Rack Elevations" no={2}>
        <div className="flex h-full flex-wrap items-start gap-8 p-8">
          {doc.racks.map((r) => (
            <div key={r.id} style={{ width: 240 }}>
              <RackElevation rack={r} />
            </div>
          ))}
        </div>
      </PrintSheet>

      {/* Schematic sheets */}
      {doc.sheets.map((s, i) => {
        const devices = doc.devices.filter((d) => d.sheet === s.id);
        const conns = doc.connections.filter((c) => {
          const a = doc.devices.find((d) => d.id === c.from);
          const b = doc.devices.find((d) => d.id === c.to);
          return a && b && a.sheet === s.id && b.sheet === s.id;
        });
        const { k, ox, oy } = sheetScale(devices);
        return (
          <PrintSheet key={s.id} doc={doc} title={s.title} no={i + 3}>
            <div style={{ transform: `scale(${k})`, transformOrigin: "top left", position: "absolute", left: 20, top: 20 }}>
              <div style={{ position: "relative", width: 3000, height: 2200, marginLeft: -ox, marginTop: -oy }}>
                <Wires devices={doc.devices} conns={conns} />
                {devices.map((d) => (
                  <BlockView key={d.id} d={d} print />
                ))}
              </div>
            </div>
          </PrintSheet>
        );
      })}
    </div>
  );
}

function PrintStyles() {
  return (
    <style>{`
      .print-only { display: none; }
      @media print {
        @page { size: 17in 11in; margin: 0.25in; }
        body { background: #fff !important; }
        header, footer, nav, .no-print { display: none !important; }
        .print-only { display: block !important; }
        .print-sheet { page-break-after: always; break-after: page; }
        .print-sheet:last-child { page-break-after: auto; }
      }
    `}</style>
  );
}
